import DefaultLayout from "@/layouts/DefaultLayout";
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import { NextSeo } from "next-seo";
import usePagination from "@/hooks/usePagination";
import {
  ColorType,
  FireProtectionMaterial,
  FireResistance,
  FlamePreventionRegulation,
  Formaldehyde,
  MakerWithCategoriesResponse,
  ProductKind,
  ProductVariationWithProductResponse,
} from "@/web-client/api";
import client from "@/utils/api/client";
import InfiniteScroll from "react-infinite-scroll-component";
import Circular from "@/components/Circular";
import useQuerySearchParameter from "@/hooks/useSearchQueryParameter";
import ProductVariationItemList from "@/features/product_variation/components/ProductVariationItemList";
import MakerHeader from "@/features/maker/components/MakerHeader";
import publicPath from "@/utils/publicPath";
import { useMemo } from "react";
import MakerSearchGroup from "@/features/maker/components/MakerSearchGroup";
import { ProductSearchQueryParameter } from "@/types/ProductSearchQueryParameter";
import NotFoundSearchView from "@/components/NotFoundSearchView";
import NavigationProductSidebar from "@/components/NavigationProductSidebar";
import { CompanyTabState } from "@/hooks/useCompanyTabs";
import { notFoundWrapper } from "@/utils/notFoundWrapper";
import usePageViewOnSearch from "@/hooks/usePageViewOnSearch";
import ProductVariationItem from "@/features/product_variation/components/ProductVariationItem";

interface Props {
  maker: MakerWithCategoriesResponse;
}

const MakerBuildingMaterialsPage: NextPage<Props> = ({
  maker,
}): JSX.Element => {
  usePageViewOnSearch(maker.id);

  const { query, queryController } =
    useQuerySearchParameter<ProductSearchQueryParameter>(
      `/makers/${maker.id}/building_materials`,
    );
  const {
    category_id,
    keyword,
    colors,
    fire_protection_material,
    flame_prevention_regulation,
    formaldehyde,
    fire_registance,
    material_id,
    min_width,
    max_width,
    min_height,
    max_height,
    min_depth,
    max_depth,
    min_price,
    max_price,
    in_stock,
    search_condition_value_ids,
    sort,
  } = query;

  const categoryId = useMemo<number | undefined>(() => {
    return category_id ? Number(category_id) : undefined;
  }, [category_id]);

  const inStock = useMemo(
    () => (in_stock ? Boolean(in_stock) : false),
    [in_stock],
  );

  const {
    data: productVariations,
    next,
    hasNext,
    dataLength,
    totalCount,
    notFound,
    updateItem,
  } = usePagination<ProductVariationWithProductResponse>(
    queryController.toPath(),
    20,
    async ({ limit, page }) => {
      const { data } = await client.makersIdProductVariationsGet({
        id: maker.id,
        keyword,
        kind: ProductKind.BUILDING_MATERIAL,
        categoryId,
        colorTypeIds: colors
          ? colors.split(",").map((v) => Number(v) as ColorType)
          : [],
        searchConditionValueIds: search_condition_value_ids
          ? search_condition_value_ids.split(",").map((v) => Number(v))
          : undefined,
        fireProtectionMaterial: fire_protection_material
          ? (Number(fire_protection_material) as FireProtectionMaterial)
          : undefined,
        flamePreventionRegulation: flame_prevention_regulation
          ? (Number(flame_prevention_regulation) as FlamePreventionRegulation)
          : undefined,
        formaldehyde: formaldehyde
          ? (Number(formaldehyde) as Formaldehyde)
          : undefined,
        fireResistance: fire_registance
          ? (Number(fire_registance) as FireResistance)
          : undefined,
        materialId: material_id ? Number(material_id) : undefined,
        minWidth: min_width ? Number(min_width) : undefined,
        maxWidth: max_width ? Number(max_width) : undefined,
        minHeight: min_height ? Number(min_height) : undefined,
        maxHeight: max_height ? Number(max_height) : undefined,
        minDepth: min_depth ? Number(min_depth) : undefined,
        maxDepth: max_depth ? Number(max_depth) : undefined,
        minPrice: min_price ? Number(min_price) : undefined,
        maxPrice: max_price ? Number(max_price) : undefined,
        inStock,
        sort: sort || undefined,
        limit,
        offset: limit * (page - 1),
      });

      return data;
    },
  );

  return (
    <DefaultLayout hiddenFooter={true}>
      <NextSeo
        title={`${maker.name} | メーカー`}
        description={`${maker.name}の建材一覧です。`}
        openGraph={
          maker.logo ? { images: [{ url: maker.logo.urls.original }] } : {}
        }
        twitter={{
          cardType: "summary",
        }}
        canonical={publicPath(`/makers/${maker.id}/building_materials`)}
      />
      <MakerSearchGroup
        maker={maker}
        sidebar={
          <NavigationProductSidebar
            kind={ProductKind.BUILDING_MATERIAL}
            categories={maker.categories}
            query={query}
            queryController={queryController}
            categoryId={categoryId}
          />
        }
        showCondition={false}
        query={query}
        queryController={queryController}
        totalCount={totalCount}
        headerView={<MakerHeader maker={maker} />}
        tabState={CompanyTabState.BuildingMaterial}
      >
        <>
          <InfiniteScroll
            style={{ overflow: "visible" }}
            dataLength={dataLength}
            next={next}
            hasMore={hasNext}
            loader={<Circular />}
          >
            <ProductVariationItemList
              productVariations={productVariations}
              onRender={(variation) => (
                <ProductVariationItem
                  productVariation={variation}
                  mutate={(updated) => updateItem(updated, false)}
                />
              )}
            />
          </InfiniteScroll>

          {notFound && <NotFoundSearchView />}
        </>
      </MakerSearchGroup>
    </DefaultLayout>
  );
};

export default MakerBuildingMaterialsPage;

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  return notFoundWrapper(async () => {
    const { data: maker } = await client.makersIdCategoriesGet({
      id: Number(params.id),
    });

    // 建材がなければ、インテリアにリダイレクト
    if (maker.building_material_product_variations_count < 1) {
      return {
        redirect: {
          destination: `/makers/${maker.id}/interiors`,
          permanent: false,
        },
      };
    }

    return {
      props: {
        maker,
      },
      revalidate: 1,
    };
  });
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: "blocking",
});
